import { useTranslation } from 'react-i18next';
import { Copy } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { DAY_KEYS } from '@/pages/public/booking-page/helpers';
import type { DayKey, TimeRange } from '@/pages/public/booking-page/types';

const WEEKDAYS: DayKey[] = ['mon', 'tue', 'wed', 'thu', 'fri'];

export function HoursEditor({ value, onChange }: { value: Record<DayKey, TimeRange[]>; onChange: (next: Record<DayKey, TimeRange[]>) => void }) {
  const { t } = useTranslation(['adminBookingPage', 'bookingPage']);
  const set = (day: DayKey, ranges: TimeRange[]) => onChange({ ...value, [day]: ranges });

  return (
    <div className="flex flex-col" data-testid="hours-editor">
      {DAY_KEYS.map((day) => {
        const range = value[day]?.[0];
        const dayName = t(`bookingPage:hours.days.${day}`);
        return (
          <div key={day} className="flex flex-col gap-2.5 border-t border-line py-3 first:border-t-0 sm:flex-row sm:items-center sm:gap-4" data-testid={`hours-day-${day}`}>
            <div className="flex items-center gap-3 sm:w-44">
              <Switch
                checked={!!range}
                aria-label={dayName}
                onCheckedChange={(on) => set(day, on ? [{ from: '09:00', to: '17:00' }] : [])}
              />
              <span className="flex-1 text-[15px] font-medium text-ink sm:flex-none">{dayName}</span>
              {!range && <span className="text-body text-soft sm:hidden">{t('sections.closed')}</span>}
            </div>
            {range ? (
              <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center">
                <label className="flex h-11 items-center justify-between gap-2 rounded-md border border-line bg-surface px-3">
                  <span className="text-body-xs text-soft">{t('sections.opens')}</span>
                  <input type="time" value={range.from} aria-label={`${dayName} ${t('sections.opens')}`} onChange={(e) => set(day, [{ ...range, from: e.target.value }])} className="bg-transparent text-[15px] tabular-nums text-ink outline-none" />
                </label>
                <span className="hidden text-soft sm:inline">–</span>
                <label className="flex h-11 items-center justify-between gap-2 rounded-md border border-line bg-surface px-3">
                  <span className="text-body-xs text-soft">{t('sections.closes')}</span>
                  <input type="time" value={range.to} aria-label={`${dayName} ${t('sections.closes')}`} onChange={(e) => set(day, [{ ...range, to: e.target.value }])} className="bg-transparent text-[15px] tabular-nums text-ink outline-none" />
                </label>
              </div>
            ) : (
              <span className="hidden text-body text-soft sm:inline">{t('sections.closed')}</span>
            )}
          </div>
        );
      })}
      <button
        type="button"
        onClick={() => {
          const monday = value.mon ?? [];
          const next = { ...value };
          WEEKDAYS.forEach((d) => { next[d] = monday.map((r) => ({ ...r })); });
          onChange(next);
        }}
        className="mt-1 inline-flex min-h-[44px] items-center gap-1.5 self-start text-body font-medium text-brand-500 hover:text-brand-700"
      >
        <Copy className="h-3.5 w-3.5" />
        {t('sections.copyMonday')}
      </button>
    </div>
  );
}
